import React from 'react';
import { X, Trash2, MessageSquarePlus } from 'lucide-react';

export interface UserFeedbacksModalProps {
  userFeedbacksModalId: string | null;
  setUserFeedbacksModalId: (id: string | null) => void;
  feedbacksList: any[];
  isAdmin: boolean;
  feedbackToDelete: string | null;
  setFeedbackToDelete: (id: string | null) => void;
  confirmDeleteFeedback: () => void;
}

export const UserFeedbacksModal: React.FC<UserFeedbacksModalProps> = ({
  userFeedbacksModalId,
  setUserFeedbacksModalId,
  feedbacksList,
  isAdmin,
  feedbackToDelete,
  setFeedbackToDelete,
  confirmDeleteFeedback,
}) => {
  return (
    <>
      {/* Feedback Delete Confirmation Modal */}
      {feedbackToDelete && (
        <div className="fixed inset-0 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-200 pointer-events-auto" style={{ zIndex: 9999999 }}>
          <div className="bg-[#0a0e27]/90 border border-[#ff3d67]/30 p-6 rounded-2xl w-full max-w-[320px] shadow-[0_0_50px_rgba(255,61,103,0.2)] flex flex-col relative">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="w-12 h-12 rounded-full bg-[#ff3d67]/10 flex items-center justify-center text-[#ff3d67] mb-2">
                <Trash2 size={24} />
              </div>
              <h3 className="text-[#00d9ff] text-[14px] font-bold uppercase tracking-[2px]">Confirmar Exclusão</h3>
              <p className="text-[#666666] text-[11px] leading-relaxed">
                Tem certeza que deseja excluir este feedback permanentemente?
              </p>
              <div className="flex w-full gap-3 pt-2">
                <button 
                  onClick={() => setFeedbackToDelete(null)}
                  className="flex-1 py-2.5 rounded-lg border border-[#00d9ff]/20 text-[#666666] text-[10px] font-bold uppercase tracking-wider hover:bg-white/5 transition-all"
                >
                  Não
                </button>
                <button 
                  onClick={confirmDeleteFeedback}
                  className="flex-1 py-2.5 rounded-lg bg-[#ff3d67] text-white text-[10px] font-bold uppercase tracking-wider hover:bg-[#ff3d67]/80 transition-all shadow-[0_0_15px_rgba(255,61,103,0.3)]"
                >
                  Sim, Excluir
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Feedbacks Modal */}
      {userFeedbacksModalId && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <div className="bg-[#0a0e27] border border-[#00d9ff]/50 rounded-xl p-4 md:p-6 w-full max-w-2xl font-mono max-h-[90vh] flex flex-col relative z-[10000]">
            <div className="flex items-center justify-between mb-4 border-b border-white/10 pb-4">
              <h3 className="text-sm font-bold text-[#00d9ff] tracking-widest uppercase flex items-center gap-2">
                <MessageSquarePlus size={16} /> Feedbacks do Usuário
              </h3>
              <button onClick={() => setUserFeedbacksModalId(null)} className="text-white/50 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-4">
              {feedbacksList.filter(f => f.userId === userFeedbacksModalId).length === 0 ? (
                <div className="text-center py-8 text-[#666] text-[10px] uppercase tracking-wider">
                  Nenhum feedback enviado por este usuário.
                </div>
              ) : (
                feedbacksList.filter(f => f.userId === userFeedbacksModalId).map(fb => (
                  <div key={fb.id} className="bg-black/40 border border-white/5 rounded-lg p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="text-[10px] text-[#00ffa3] font-bold">
                        Enviado em: {new Date(fb.createdAt).toLocaleString()}
                      </div>
                    </div>
                    {fb.description && (
                      <p className="text-[11px] text-white/80 mt-1 mb-3 bg-white/5 p-3 rounded leading-relaxed border border-white/5">
                        {fb.description}
                      </p>
                    )}
                    {(fb.imageUrl || fb.imageBase64) && (
                      <a href={fb.imageUrl || fb.imageBase64} target="_blank" rel="noopener noreferrer" className="block w-full border border-white/10 rounded overflow-hidden hover:border-[#00ffa3]/50 transition-colors relative group">
                        <img src={fb.imageUrl || fb.imageBase64} alt="Feedback Screenshot" className="w-full object-contain bg-black/80" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity backdrop-blur-sm">
                          <span className="text-[11px] text-[#00ffa3] font-bold uppercase tracking-wider bg-black/80 px-4 py-2 rounded border border-[#00ffa3]/30">Abrir Imagem em Nova Guia</span>
                        </div>
                      </a>
                    )}
                    
                    {isAdmin && (
                      <div className="mt-3 pt-3 border-t border-white/5 flex justify-end">
                        <button
                          onClick={(e) => { e.stopPropagation(); setFeedbackToDelete(fb.id); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-500 border border-red-500/30 rounded text-[9px] font-bold uppercase tracking-wider transition-colors"
                        >
                          <Trash2 size={12} /> Excluir Feedback
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
